/**
 * How complete an avatar is, derived from what it actually has.
 *
 * The backend has no readiness field, so this is computed in the browser from
 * the template itself. Every item is something the user can act on, and the
 * checklist is shown alongside the score so the number is never a mystery.
 */
export const readinessChecklist = ({
  avatarUrl,
  avatarBase64,
  description,
  sections = [],
  examples = [],
  tags = [],
  outputFormat,
  jsonSchemaFields = [],
}) => {
  const items = [
    {
      id: "image",
      label: "Has an image",
      hint: "A portrait makes it recognisable in lists",
      done: Boolean(avatarBase64 || avatarUrl),
    },
    {
      id: "description",
      label: "Has a description",
      hint: "Explains what this avatar is for",
      done: Boolean(description?.trim()),
    },
    {
      id: "blocks",
      label: "At least 3 instruction blocks",
      hint: "Identity, tone and boundaries at a minimum",
      done: sections.length >= 3,
    },
    {
      id: "examples",
      label: "At least one example",
      hint: "Examples are what make responses consistent",
      done: examples.length >= 1,
    },
    {
      id: "tags",
      label: "Tagged",
      hint: "Tags are how avatars get found",
      done: tags.length > 0,
    },
    {
      id: "output",
      label: "Output format configured",
      hint:
        outputFormat === "json"
          ? "JSON mode needs a schema to be useful"
          : "Any mode counts once it is set",
      // JSON without a schema is the one mode that is set but not finished.
      done: outputFormat === "json" ? jsonSchemaFields.length > 0 : Boolean(outputFormat),
    },
  ];

  const done = items.filter((i) => i.done).length;

  return {
    items,
    done,
    total: items.length,
    percent: Math.round((done / items.length) * 100),
  };
};

/** The one-liner shown next to the ring. */
export const readinessMessage = (percent) => {
  if (percent === 100) return "This avatar is ready to publish.";
  if (percent >= 80) return "Almost ready to publish.";
  if (percent >= 50) return "Coming along — a few things left.";
  return "Just getting started.";
};
